/**
 * Stripe Checkout, billing portal, and match transactions.
 */
import { auth } from '../auth/firebaseAuth';
import { getPublicSiteOrigin } from '../utils/publicSiteUrl';

function getFunctionsBaseUrl() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;
  if (base) return base.replace(/\/$/, '');
  const projectId = process.env.REACT_APP_PROJECT_ID;
  if (!projectId) return null;
  const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    const emulatorPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5011';
    return `http://127.0.0.1:${emulatorPort}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

async function postStripeFunction(path, body, { idToken } = {}) {
  const base = getFunctionsBaseUrl();
  if (!base) {
    throw new Error('Stripe is not configured. Set REACT_APP_PROJECT_ID or REACT_APP_CLOUD_FUNCTIONS_URL.');
  }
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  const res = await fetch(`${base}/${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Stripe request failed (${res.status})`);
  }
  return data;
}

async function getAuthedIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required.');
  return user.getIdToken();
}

/**
 * @param {'lockedin_plus'} tierId
 * @param {{ userId: string, email?: string }} opts
 * @returns {Promise<{ url: string }>}
 */
export async function createSubscriptionCheckout(tierId, {
  userId,
  email,
  checkoutSource,
  checkoutDetail,
}) {
  const idToken = await getAuthedIdToken();
  const origin = getPublicSiteOrigin();
  const basePath = window.location.pathname.replace(/\/$/, '') || '';
  const body = {
    tier: tierId,
    userId,
    email: email || null,
    successUrl: `${origin}${basePath}/?subscription=success&tier=${encodeURIComponent(tierId)}&session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl: `${origin}${basePath}/?subscription=cancel`,
  };
  if (checkoutSource) body.checkoutSource = checkoutSource;
  if (checkoutDetail) body.checkoutDetail = checkoutDetail;
  return postStripeFunction('create_stripe_checkout', body, { idToken });
}

/**
 * @param {{ userId: string }} opts
 * @returns {Promise<{ url: string }>}
 */
export async function createBillingPortalSession({ userId }) {
  const idToken = await getAuthedIdToken();
  const origin = getPublicSiteOrigin();
  return postStripeFunction('create_stripe_portal', {
    userId,
    returnUrl: `${origin}/`,
  }, { idToken });
}

/**
 * @param {{ walletCreditCents: number, billingCountry?: string }} opts
 * @returns {Promise<{ url: string, quote?: object }>}
 */
export async function createWalletDepositCheckout({ walletCreditCents, billingCountry }) {
  const origin = getPublicSiteOrigin();
  const idToken = await getAuthedIdToken();
  return postStripeFunction(
    'create_wallet_deposit',
    {
      walletCreditCents,
      billingCountry: billingCountry || undefined,
      successUrl: `${origin}/?deposit=success&entered=1&session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${origin}/?deposit=cancel`,
    },
    { idToken }
  );
}

/**
 * Server-authoritative gross-up quote (wallet credit, payment processing, total charge).
 * @param {number} walletCreditCents
 * @returns {Promise<{
 *   walletCreditCents: number,
 *   processingFeeCents: number,
 *   checkoutChargeCents: number,
 *   walletCreditDollars: number,
 *   processingFeeDollars: number,
 *   totalChargeDollars: number,
 *   depositModel: string,
 * }>}
 */
export async function quoteWalletDeposit(walletCreditCents) {
  const idToken = await getAuthedIdToken();
  return postStripeFunction(
    'quote_wallet_deposit',
    { walletCreditCents },
    { idToken }
  );
}

/**
 * Record H2H match settlement for auditing (server-side Firestore write).
 */
export async function recordMatchTransaction({
  matchId,
  player1Id,
  player2Id,
  totalPot,
  platformFee,
  payoutAmount,
  winnerId,
  stripePaymentIntentId,
}) {
  const idToken = await getAuthedIdToken();
  return postStripeFunction(
    'record_match_transaction',
    {
      matchId,
      player1Id,
      player2Id,
      totalPot,
      platformFee,
      payoutAmount,
      winnerId: winnerId || null,
      stripePaymentIntentId: stripePaymentIntentId || null,
    },
    { idToken }
  );
}

export function getStripePublishableKey() {
  return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null;
}

export function isStripeConfigured() {
  return Boolean(getFunctionsBaseUrl() && getStripePublishableKey());
}

/**
 * Force-sync subscription from Stripe after Checkout return (before webhook lands).
 * @param {string} [sessionId] — from ?session_id= on success URL
 */
export async function syncSubscriptionAfterCheckout(sessionId) {
  const idToken = await getAuthedIdToken();
  return postStripeFunction(
    'sync_stripe_subscription',
    { sessionId: sessionId || null },
    { idToken }
  );
}

/**
 * Force-sync wallet deposit from Stripe after Checkout return (before webhook lands).
 * @param {string} sessionId — from ?session_id= on success URL
 */
export async function syncWalletDepositAfterCheckout(sessionId) {
  const idToken = await getAuthedIdToken();
  return postStripeFunction(
    'sync_wallet_deposit',
    { sessionId },
    { idToken }
  );
}

// Canonical predicate lives in utils/platformFee; re-exported here so existing
// import sites keep working while there is a single source of truth.
export { isActiveSubscriptionStatus } from '../utils/platformFee';
